<template>
  <form
    ref="tailoredLeadsForm"
    @submit.prevent
  >
    <div class="wrapper">
      <p class="section-title">
        Tell us about you and your ideal prospects
      </p>
      <b-row class="px-1">
        <b-col
          md="6"
          class="left-col"
        >
          <b-form-group
            class="custom-form-group-styles"
            label="Website *"
            label-class="w-fit-content custom-label-style"
          >
            <b-form-input
              :value="website"
              :state="websiteErrorState"
              placeholder="Your website url like www.example.com"
              @keydown.space.prevent
              @paste="removeSpaces"
              @input="handleWebsiteChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          class="right-col"
        >
          <b-form-group class="position-up">
            <span class="custom-label-style">Desired countries</span>
            <custom-select
              id="desired-countries-dropdown"
              v-model="desiredCountries"
              multiple
              show-arrow-icon
              placeholder="Enter the countries where you want to focus your prospect search"
              no-data-text="No country found."
              theme="grey"
              size="small"
              :should-scroll-on-focus="true"
              :clear-search-on-select="true"
              :option-groups="desiredCountriesOptions"
              @search="handleSearchCountries"
              @select="(countries) => desiredCountries = countries"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="px-1">
        <b-col
          class="left-col"
          md="6"
        >
          <b-form-group
            class="tag-form-group"
            label-class="w-fit-content custom-label-style"
          >
            <template #label>
              Domains of Your Existing or Ideal Customers * <span class="grey-label-text">(comma separated domains)</span>
            </template>
            <b-form-tags
              v-model="lookalikeLeads"
              separator=","
              tag-class="custom-tag"
              remove-on-delete
              :state="lookalikeLeadsErrorState"
              duplicate-tag-text="Duplicate prospect domain"
              invalid-tag-text=""
              :limit="10"
              limit-tags-text=""
              :placeholder="!lookalikeLeads.length ? `Share the domains of businesses that are similar to your target prospects, 10 max` : ''"
              input-id="lookalike-lead-input"
              @input="handleLookalikeLeadsChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          class="right-col"
        >
          <b-form-group
            class="tag-form-group"
            label-class="w-fit-content custom-label-style"
          >
            <template #label>
              Desired industries <span class="grey-label-text">(select industries)</span>
            </template>
            <custom-select
              id="desired-industries-dropdown"
              v-model="desiredIndustries"
              multiple
              show-arrow-icon
              placeholder="Select the industries you want to target for your prospects"
              no-data-text="No industries found."
              theme="grey"
              size="small"
              :should-scroll-on-focus="true"
              :option-groups="desiredIndustriesOptions"
              :is-group-expandable="true"
              :clear-search-on-select="true"
              @search="handleSearchIndustries"
              @select="(industries) => desiredIndustries = industries"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="px-1">
        <b-col
          md="6"
          class="left-col"
        >
          <b-row>
            <b-col
              class="desired-keywords-column"
            >
              <b-form-group
                class="tag-form-group desired-keywords"
                label-class="w-fit-content custom-label-style"
              >
                <template #label>
                  Desired keywords describing the prospects <span class="grey-label-text">(comma separated)</span>
                </template>
                <b-form-tags
                  v-model="desiredKeywords"
                  input-id="desired-keywords-input"
                  separator=","
                  tag-class="custom-tag"
                  :limit="10"
                  limit-tags-text=""
                  remove-on-delete
                  duplicate-tag-text="Duplicate keyword"
                  :placeholder="!desiredKeywords.length ? `Enter keywords describing your target prospects, 10 max` : ''"
                  @input="(val) =>handleFormTagLimit(val, 'desired-keywords-input')"
                />
              </b-form-group>
            </b-col>
            <b-col
              md="3"
              class="match-rule"
            >
              <b-form-group
                class="tag-form-group"
                label-class="w-fit-content custom-label-style match-rule-form-group"
              >
                <template #label>
                  Match rule
                  <info-icon
                    id="tooltip-icon"
                    title="Tooltip"
                    class="info-icon"
                  />
                  <b-tooltip
                    target="tooltip-icon"
                    custom-class="match-rule-tooltip"
                    triggers="hover"
                    boundary="window"
                    placement="rightbottom"
                  >
                    <strong>Any (Broader)</strong>: Returns results that match any of your keywords. This widens the search and shows more prospects. <br>
                    <strong>All (Specific)</strong>: Returns results that match all your keywords. This narrows the search to more precise prospects. <br>
                    <strong>None</strong>: Excludes results that contain any of your keywords.
                  </b-tooltip>
                </template>
                <custom-select
                  id="match-rule"
                  v-model="matchRule"
                  :is-searchable="false"
                  show-arrow-icon
                  :is-selection-removable="false"
                  placeholder="Select the match rule for your keywords"
                  no-data-text="No rule found."
                  theme="grey"
                  size="small"
                  :should-scroll-on-focus="true"
                  :close-on-select="true"
                  :option-groups="desiredKeywordsMatchRuleOptions"
                  @select="(newRule) => matchRule = newRule"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col
          md="6"
          class="right-col"
        >
          <b-form-group
            class="tag-form-group"
            label-class="w-fit-content custom-label-style"
          >
            <template #label>
              Minimum relevancy score <span class="grey-label-text">(default 0.7, decrease to get more prospects)</span>
            </template>
            <custom-select
              id="relevancy-score-dropdown"
              v-model="relevancyScore"
              :is-searchable="false"
              show-arrow-icon
              :is-selection-removable="false"
              placeholder="Select the relevancy score for your results"
              no-data-text="No score found."
              theme="grey"
              size="small"
              :should-scroll-on-focus="true"
              :close-on-select="true"
              :option-groups="relevancyScoreOptions"
              @select="(newScore) => relevancyScore = newScore"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <div
        class="w-100 d-flex"
        :class="errorMsg ? `justify-content-between` : `justify-content-end`"
      >
        <p
          v-if="errorMsg"
          class="error-text"
        >
          {{ errorMsg }}
        </p>
        <div class="button-container">
          <b-button
            variant="light"
            class="clear-btn"
            @click="clearData"
          >
            Clear form
          </b-button>
          <b-button
            :disabled="isFormLoading || (isFormDataSame && isRequestSentUsingStoredData)"
            variant="light"
            :class="{'submit-btn': true, 'reduce-padding': !isFormLoading}"
            @click.prevent="handleFormSubmit"
          >
            <b-spinner
              v-if="isFormLoading"
              class="mr-1"
              small
              label="Loading..."
            />
            <span v-if="isFormLoading">Loading...</span>
            <span v-else>Get 5 tailored prospects
              <span class="credit-block">10 <coin-icon /></span>
            </span>
          </b-button>
        </div>
      </div>
    </div>
  </form>
</template>
<script>
import {
  BFormInput, BFormGroup, BCol, BRow, BButton, BFormTags, BSpinner, BTooltip,
} from 'bootstrap-vue'
import InfoIcon from '@/@core/assets/svg-components/InfoIcon.vue'
import CustomSelect from '@/@core/components/custom-select/CustomSelect.vue'
import store from '@/store'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { validatorUrl } from '@/@core/utils/validations/validators'
import { isEqual, debounce } from 'lodash'
import { SET_REQUEST_SENT_FLAG, SET_TAILORED_LEADS_FORM_DATA, FETCH_DESIRED_INDUSTIES } from '@/store/modules/leadai.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import CoinIcon from '@/@core/assets/svg-components/CoinIcon.vue'
import { apiToastError } from '@/@core/utils/toast'
import {
 ERROR_MESSAGES, TAILORED_LEADS_FORM_STORAGE_KEY, relevancyScoreOptions, desiredKeywordsMatchRuleOptions,
} from '../../leadsAiConstants'

export default {
  name: 'TailoredLeadsForm',
  components: {
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    CustomSelect,
    BButton,
    BFormTags,
    BSpinner,
    CoinIcon,
    BTooltip,
    InfoIcon,
  },
  props: {
    isFormLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      website: '',
      relevancyScore: ['70%'],
      desiredCountries: [],
      desiredKeywords: [],
      lookalikeLeads: [],
      desiredIndustries: [],
      desiredCountriesOptions: [],
      desiredIndustriesOptions: [],
      allIndustries: [],
      previousPayload: {},
      errorFlags: {},
      allCountries: [],
      errorMsg: '',
      matchRule: ['All (Specific)'],
      relevancyScoreOptions,
      desiredKeywordsMatchRuleOptions,
    }
  },
  computed: {
    websiteErrorState() {
      if (!this.errorFlags?.website) {
        return null
      }
      return this.errorFlags.website.required !== null || this.errorFlags.website.invalid !== null ? false : null
    },
    lookalikeLeadsErrorState() {
      if (!this.errorFlags?.lookalikeLeads) {
        return null
      }
      return this.errorFlags?.lookalikeLeads?.required !== null || this.errorFlags.lookalikeLeads.invalid !== null ? false : null
    },
    payload() {
      return {
        website: this.website,
        desiredCountries: this.desiredCountries,
        desiredKeywords: this.desiredKeywords,
        lookalikeLeads: this.lookalikeLeads,
        desiredIndustries: this.desiredIndustries,
        matchRule: this.matchRule,
        relevancyScore: this.relevancyScore,
      }
    },
    isFormDataSame() {
      return isEqual(this.previousPayload, this.payload)
    },
    isRequestSentUsingStoredData() {
      return store.state.leadai.tailoredLeadsFormData?.isRequestSentUsingStoredData || false
    },
  },
  watch: {
    errorFlags(val) {
      this.updateErrorMsg(this.getErrorFlags(val))
    },
    payload(val) {
      this.debouncedInputTrack(val, this.errorFlags)
      this.debouncedInputPersist(val, this.errorFlags)
    },
    isFormDataSame(val) {
      if (!val && this.isRequestSentUsingStoredData) {
        store.commit(SET_REQUEST_SENT_FLAG, false)
      }
    },
  },
  created() {
    this.getAllCountries()
    this.getAllIndustries()

    const existingData = localStorage.getItem(TAILORED_LEADS_FORM_STORAGE_KEY)

    if (existingData) {
      const persistedInputData = JSON.parse(existingData)
      const persistedInputs = persistedInputData.inputs
      this.errorFlags = persistedInputData.errorFlags
      if (persistedInputs) {
        this.website = persistedInputs.website
        this.desiredCountries = persistedInputs.desiredCountries
        this.desiredKeywords = persistedInputs.desiredKeywords
        this.lookalikeLeads = persistedInputs.lookalikeLeads
        this.desiredIndustries = persistedInputs.desiredIndustries
        this.matchRule = persistedInputs.matchRule ?? ['All (Specific)']
        this.relevancyScore = persistedInputs.relevancyScore
      }
    }

    this.previousPayload = { ...this.payload }
  },
  mounted() {
    const label = document.getElementsByClassName('desired-country-label')
    if (label?.length) {
      label[0].addEventListener('click', e => e.preventDefault())
    }
  },
  methods: {
    clearData() {
      this.website = ''
      this.desiredCountries = []
      this.desiredKeywords = []
      this.lookalikeLeads = []
      this.desiredIndustries = []
      this.errorMsg = ''
      this.errorFlags = {}
      this.relevancyScore = ['70%']
      this.matchRule = ['All (Specific)']

      this.$refs.tailoredLeadsForm.reset()

      setTimeout(() => localStorage.removeItem(TAILORED_LEADS_FORM_STORAGE_KEY), 650)
    },
    allowOnlyBackspace(e) {
      if (e.key !== 'Backspace') {
        e.preventDefault()
      }
    },
    isWebsiteErrorAllowed(flag) {
      return this.websiteErrorState !== null && flag !== null
    },
    isLeadsErrorAllowed(flag) {
      return this.lookalikeLeadsErrorState !== null && flag !== null
    },
    getErrorFlags(val) {
      return {
        websiteRequired: this.isWebsiteErrorAllowed(val.website?.required),
        invalidUrl: this.isWebsiteErrorAllowed(val.website?.invalid),
        leadsRequired: this.isLeadsErrorAllowed(val.lookalikeLeads?.required),
        invalidLeads: this.isLeadsErrorAllowed(val.lookalikeLeads?.invalidLeads),
      }
    },
    handleWebsiteChange(val) {
      this.website = val
      this.errorFlags = {
        ...this.errorFlags,
        website: {
          required: !val ? false : null,
          invalid: !validatorUrl(val) ? false : null,
        },
      }
    },
    debouncedInputPersist: debounce((inputs, errorFlags) => {
      const existingData = localStorage.getItem(TAILORED_LEADS_FORM_STORAGE_KEY)

      const persistedData = existingData ? JSON.parse(existingData) : {}
      localStorage.setItem(TAILORED_LEADS_FORM_STORAGE_KEY, JSON.stringify({
        ...persistedData,
        inputs,
        errorFlags,
      }))
    }, 500),
    debouncedInputTrack: debounce(inputs => {
      analytics.track(constants.TRACKS.ACTIONS.LEAD_AI.BRAND_INPUTS_LEAD_AI_FORM_DATA, {
        formData: inputs,
      })
    }, 500),
    async handleLookalikeLeadsChange(val) {
      this.errorFlags = {
        ...this.errorFlags,
        lookalikeLeads: {
          required: val.length === 0 ? false : null,
          invalid: val.length !== 0 && !val.every(tag => validatorUrl(tag)) ? false : null,
        },
      }
      await this.handleFormTagLimit(val, 'lookalike-lead-input')
    },
    async handleFormTagLimit(val, inputId) {
      const inputEl = document.getElementById(inputId)
      if (!inputEl) {
        return
      }
      if (val.length >= 10) {
        inputEl.addEventListener('keydown', this.allowOnlyBackspace)
      } else {
        inputEl.removeEventListener('keydown', this.allowOnlyBackspace)
        inputEl.focus()
      }
    },
    getAllCountries() {
      store.dispatch(GET_COUNTRIES, { fromCountryLocaleMap: true }).then(res => {
        this.allCountries = res.data.data.COUNTRIES
        this.desiredCountriesOptions = [{
          title: '',
          options: this.allCountries,
        }]
      }).catch(() => {
        apiToastError(ERROR_MESSAGES.FETCH_COUNTRIES_FAILED)
      })
    },
    getAllIndustries() {
      store.dispatch(FETCH_DESIRED_INDUSTIES).then(res => {
        this.desiredIndustriesOptions = [
          {
            title: 'Popular Industries',
            options: res.data.popularIndustries.map(industryCategory => ({
              industry: industryCategory.industryCategory,
              options: industryCategory.industries,
            })),
          },
          {
            title: 'All Industries',
            options: res.data.desiredIndustries.industries.map(industryCategory => ({
              industry: industryCategory.industryCategory,
              options: industryCategory.industries,
            })),
          },
        ]
        this.allIndustries = this.desiredIndustriesOptions
      }).catch(() => {
        apiToastError(ERROR_MESSAGES.FETCH_DESIRED_INDUSTRIES_FAILED)
      })
    },
    handleSearchIndustries(searchString) {
      this.desiredIndustriesOptions = this.allIndustries
        .map(industryCategory => ({
          title: industryCategory.title,
          options: industryCategory.options
            .map(subIndustry => ({
              ...subIndustry,
              options: subIndustry.options
                ? subIndustry.options.filter(option => option.toLowerCase().includes(searchString.toLowerCase()))
                : [],
            }))
            .filter(subIndustry => subIndustry.options.length > 0),
        }))
        .filter(category => category.options.length > 0)
    },

    removeSpaces(e) {
      e.preventDefault()
      const left = e.target.value.substring(0, e.target.selectionStart)
      const right = e.target.value.substring(e.target.selectionEnd, e.target.value.length)
      const pasted = (e.dataTransfer || e.clipboardData).getData('text').replace(/ /g, '')
      const val = left + pasted + right
      e.target.value = val
      this.handleWebsiteChange(val)
    },
    handleSearchCountries(searchString) {
      this.desiredCountriesOptions = [
        {
          title: '',
          options: this.allCountries.filter(country => country.toLowerCase()
          .indexOf(searchString.toLowerCase()) !== -1),
        },
      ]
    },
    async handleFormSubmit() {
      const isValid = this.validate()
      if (isValid) {
        if (!this.isRequestSentUsingStoredData) {
          store.commit(SET_TAILORED_LEADS_FORM_DATA, {
            formData: {
              ...this.payload,
            },
          })
          this.$emit('form-submit', {
            ...this.payload,
            relevancyScore: Number(this.relevancyScore[0].replace('%', '')) / 100,
          })
          this.previousPayload = { ...this.payload }
          analytics.track(constants.TRACKS.ACTIONS.LEAD_AI.BRAND_CLICKS_GET_TAILORED_LEADS_BTN, {
            formData: this.payload,
          })
        } else {
          this.$emit('duplicate-search')
        }
      }
    },
    validate() {
      const websiteRequired = !this.website
      const leadsRequired = this.lookalikeLeads.length === 0
      const invalidUrl = !validatorUrl(this.website)
      const invalidLeads = !this.lookalikeLeads.every(lead => validatorUrl(lead))

      this.errorFlags = {
        website: {
          required: websiteRequired ? false : null,
          invalid: invalidUrl ? false : null,
        },
        lookalikeLeads: {
          required: leadsRequired ? false : null,
          invalid: invalidLeads ? false : null,
        },
      }

      return !websiteRequired && !invalidUrl && !leadsRequired && !invalidLeads
    },
    updateErrorMsg(flags) {
      const {
        websiteRequired, leadsRequired, invalidLeads, invalidUrl,
      } = flags

      const conditionsToMessages = [
        { condition: websiteRequired && leadsRequired, message: 'Please fill in your "Website url" and "Domains of Your Existing or Ideal Customers" fields.' },
        { condition: websiteRequired && invalidLeads, message: 'Please fill in your "Website url" and enter valid "Lookalike prospect domains" field.' },
        { condition: invalidUrl && leadsRequired, message: 'Please enter valid "Website url" and fill "Domains of Your Existing or Ideal Customers" field.' },
        { condition: invalidUrl && invalidLeads, message: 'Please enter valid "Website url" and "Lookalike prospect domains" fields.' },
        { condition: websiteRequired, message: 'Please fill in your "Website url" field.' },
        { condition: leadsRequired, message: 'Please fill "Domains of Your Existing or Ideal Customers" field.' },
        { condition: invalidUrl, message: 'Please enter valid "Website url" field.' },
        { condition: invalidLeads, message: 'Please enter valid "Domains of Your Existing or Ideal Customers" field.' },
      ]
      for (const conditionItem of conditionsToMessages) {
        if (conditionItem.condition) {
          this.errorMsg = conditionItem.message
          return
        }
      }
      this.errorMsg = ''
    },
  },
}
</script>
<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include'; // Bootstrap includes

.match-rule-tooltip {
  .arrow::before {
    border-right-color: #7163FF;
  }
  .tooltip-inner {
    background-color: #7163FF;
    color: #fff;
    font-size: 10px;
    line-height: 14px;
    min-width: 325px;
    text-align: left;
    padding: 5px 9px 5px 29px;
  }
}
.wrapper {
  background-color: #fff;
  border: 1px solid #E0E0E0;
  border-radius: 5px;
  padding: 16px 24px;
  padding-top: 26px;
  max-width: 70.4vw;

  .button-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
  }

  .clear-btn {
    background-color:#ebebeb;
    min-height: 37px;
    padding-top: 0.6rem;
  }

  .error-text {
    color: #D60909;
    font-weight: 500;
    max-width: 600px;
  }

  .left-col {
    padding: 0;
    padding-right: 1rem;
  }

  .right-col {
    padding: 0;
    padding-left: 1rem;
  }

  .desired-keywords-column {
    padding-right: 11px;
  }

  .match-rule {
    padding-left: 0;
    min-width: 143px;
  }

  .section-title {
    font-size: 16px;
    font-weight: bold;
  }

  .custom-label-style {
    display: inline-block;
    font-size: 12px;
    font-weight: 500;
    padding-bottom: 3px;
    margin-bottom: 0;
  }

  .match-rule-form-group {
    @media(max-width: 1464px) {
      margin-bottom: 17px;
    }
  }

  .w-fit-content {
    width: fit-content !important;
  }

  .custom-form-group-styles {
    input {
      font-size: 12px;
      padding: 10px 9px !important;
    }

    textarea {
      font-size: 12px;
      padding: 9px 8px !important
    }
  }

  input::placeholder {
    color: #B9B9B8;
  }

  input:placeholder-shown {
    text-overflow: ellipsis;
  }

  .position-up {
    position: relative;
    top: -2px
  }

  .tag-form-group {
    input {
      font-size: 12px;
    }

    .info-icon {
      margin-bottom: 5px;
    }

    .grey-label-text {
      width: fit-content;
      display: inline;
      font-size: inherit;
      color: $lighter-gray;
    }
    .form-control {
      padding-top: 5px;
      padding-bottom: 5px;

      .b-form-tags-input {
        min-height: 26px;
      }

      .b-form-tags-button {
        display: none;
      }
    }
    .custom-tag {
      color: $primary !important;
      font-weight: 500;
      font-size: 12px;
      display: flex !important;
      justify-content: space-between;
      background-color: $bright-gray !important;
      border-radius: 3px;
      align-items: center !important;
      margin-right: 7px;

      button.b-form-tag-remove {
        align-self: end;
      }
    }
  }

  button.submit-btn:disabled {
    cursor: not-allowed;
  }

  .submit-btn {
    background-color: $custom-secondary !important;
    color: $text-color-v2 !important;
    font-weight: 400;
    width: 234px;

    .credit-block {
      display: inline-flex;
      color: #686767;
      font-size: 12px;
      line-height: 17px;
      border-radius: 5px;
      border: 1px solid #7163FF;
      padding: 3px 6px;
      align-items: center;
      gap: 4px;
      margin-left: 8px;
    }
  }

  .reduce-padding {
    padding: 5px 11px !important;
  }
}

@media (max-width: 1024px) {
  .wrapper {
    max-width: 100%;
  }
}

@media (max-width: 767px) {
  .wrapper {
    .left-col, .right-col {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

</style>
